import type { ActionTree } from 'vuex'
import type { AutomationState } from './types'
import { formattedAPIBox, getActionBlock, getAutomationAction, getTriggers } from './utils'
import type { RootState } from '@/store/modules/types'
import { getContext } from '@/utils/general'

export const automationActions: ActionTree<AutomationState, RootState> = {
  CLEAR_EDITABLE({ commit }, selectedTab) {
    commit('clearEditable', selectedTab)
  },
  SET_EDITABLE_ITEM({ commit }, item) {
    commit('setEditableItem', item)
  },
  FIND_AND_SET_EDITABLE_TRIGGER({ commit, state }, id) {
    const triggerItem = state.triggers.items.find(item => item.id === id)
    commit('setEditableItem', triggerItem)
  },
  FIND_AND_SET_EDITABLE_ACTION({ commit, state }, id) {
    const actionBox = getAutomationAction(state.actions, id)
    commit('setEditableItem', actionBox.block)
  },
  async NEW_AUTOMATION({ commit, rootState }, name) {
    const { promoterOid } = rootState.authModule.account!
    const { $api } = getContext()

    try {
      commit('setIsAutomationCreating', true)
      const data = await $api.automation.create(promoterOid, name)
      commit('newAutomation', data)
      return data
    }
    catch (error) {
      console.log(error)
    }
    finally {
      commit('setIsAutomationCreating', false)
    }
  },
  async RENAME_AUTOMATION({ commit, rootState }, { id, name }) {
    const { promoterOid } = rootState.authModule.account!
    const { $api } = getContext()

    try {
      await $api.automation.rename(promoterOid, id, name)
      commit('renameAutomation', name)
    }
    catch (error) {
      console.log(error)
    }
  },

  async UPDATE_IS_UNIQUE({ commit, rootState }, { id, isUnique }) {
    const { promoterOid } = rootState.authModule.account!
    const { $api } = getContext()

    try {
      await $api.automation.patchChart(promoterOid, id, { isUnique })
      commit('updateIsUnique', isUnique)
    }
    catch (error) {
      console.log(error)
    }
  },

  async UPDATE_TRIGGER_CONTEXT_DATA({ state, commit, rootState }, { id, data }) {
    const { promoterOid } = rootState.authModule.account!
    const automationId = state.id!
    const meta = data?.meta ? data?.meta : {}
    const { $api } = getContext()

    const trigger = state.triggers.items.find(item => item.id === id)
    const response = await $api.automation.updateItemConfig(promoterOid, automationId, 'trigger', id, data.config)
    commit('updateContextData', { item: trigger, config: response, meta: meta })
    commit('setSidebarTab', 'actions')
  },

  async UPDATE_ACTION_CONFIG({ state, commit, rootState }, { id, data }) {
    const { promoterOid } = rootState.authModule.account!
    const automationId = state.id!
    const meta = data?.meta ? data?.meta : {}

    const actionBox = getAutomationAction(state.actions, id)
    if (!actionBox) {
      throw new Error(`Unable to find action block with id: ${id}`)
    }
    const { $api, $arNotification } = getContext()

    try {
      const response = await $api.automation.updateItemConfig(promoterOid, automationId, 'block', id, data.config)
      commit('updateContextData', { item: actionBox.block, config: response, meta: meta })
      commit('actionConfigHasUpdated')
    }
    catch (error) {
      const errObj: any = Object.assign({}, error)
      if (errObj['response'].data.message.details.includes('Operation can only be performed if chart\'s status is')) {
        $arNotification.push({ type: 'error', message: `Please change status of Automation before modifying.` })
      }
    }
  },

  async START({ state, dispatch, commit, rootState }, payload) {
    const { promoterOid } = rootState.authModule.account!
    const automationId = payload?.id ? payload.id : state.id!
    const origStatus = state.status

    // Big chunk of logic to prevent automations from starting if they are sending from unverified domains.
    // Does automation have any action.email.send actions?
    // If so, check to see whether the sender has been verified
    // If it hasn't, return early and show an $arNotification letting them know that the automation
    //    could not start due to an unauthenticated email sender domain.
    const { $api, $arNotification } = getContext()
    try {
      const automation = await $api.automation.fetchAutomation(promoterOid, automationId)
      const blockOids = automation.blocks ? Object.keys(automation.blocks) : []
      const sendBlocks: any = []
      blockOids.forEach((oid) => {
        if (automation.blocks[oid] && automation.blocks[oid].type === 'action.send.email') {
          sendBlocks.push(automation.blocks[oid])
        }
      })
      if (sendBlocks.length > 0 && sendBlocks.every((block: any) => block.config)) {
        const senderOids = sendBlocks.map((block: any) => {
          if (!block.config['promoter-property-oid']) {
            $arNotification.push({
              type: 'error',
              message: 'Automation cannot start because at least one email sender is not verified',
            })
            return false
          }
          return block?.config['promoter-property-oid'] ? block?.config['promoter-property-oid'] : null
        }).filter((item: string) => {
          return !!item
        })
        const senders = await $api.promoterProperty.searchOids(promoterOid, 'promoter-email-verification', senderOids)
        const domainsFromSenders = senders.map((item: any) => {
          const propertySplit = item.property ? item.property.split('@') : []
          if (propertySplit.length > 0 && !!propertySplit[1]) {
            return propertySplit[1]
          }
          return null
        }).filter((item: string) => {
          return !!item
        })
        const allDomains = await $api.auth.fetchDomains(promoterOid)
        const verifiedMatchingDomains = allDomains.filter((item: any) => {
          return domainsFromSenders.indexOf(item.domain) > -1 && item.verified
        })
        let allVerified = true
        domainsFromSenders.forEach((domain) => {
          const matchingDomain = verifiedMatchingDomains.find((item: any) => item.domain === domain)
          if (!matchingDomain || !matchingDomain.verified) allVerified = false
        })
        if (!allVerified) {
          $arNotification.push({
            type: 'error',
            message: 'Failed to start automation - please ensure email actions are sent from authenticated domains',
          })
          return
        }
      }
    }
    catch (e) {
      $arNotification.push({
        type: 'error',
        message: 'Failed to start automation',
      })
      console.error(e)
      return
    }

    try {
      await $api.automation.start(promoterOid, automationId)
      commit('updateStatus', 'should-start')
    }
    catch (error) {
      commit('updateStatus', origStatus)
      throw error
    }
    dispatch('FETCH_AUTOMATION_INSIGHTS', { automationOid: automationId })
    dispatch('FETCH_AUTOMATIONS')
  },
  async PAUSE({ state, commit, rootState }, payload) {
    const { promoterOid } = rootState.authModule.account!
    const automationId = payload?.id ? payload.id : state.id!
    const origStatus = state.status
    const { $api } = getContext()

    try {
      await $api.automation.pause(promoterOid, automationId)
      commit('updateStatus', 'should-pause')
    }
    catch (error) {
      commit('updateStatus', origStatus)
      throw error
    }
  },
  async STOP({ state, commit, rootState }, payload) {
    const { promoterOid } = rootState.authModule.account!
    const automationId = payload?.id ? payload.id : state.id!
    const origStatus = state.status
    const { $api } = getContext()

    try {
      await $api.automation.stop(promoterOid, automationId)
      commit('updateStatus', 'should-stop')
    }
    catch (error) {
      commit('updateStatus', origStatus)
      throw error
    }
  },
  async DELETE({ state, commit, rootState }, { id, skipListFilter = false }) {
    const { promoterOid } = rootState.authModule.account!
    const automationId = id ? id : state.id!
    const { $api } = getContext()

    await $api.automation.delete(promoterOid, automationId)

    if (!skipListFilter) {
      commit('removeAutomationFromList', automationId)
    }
  },
  async DUPLICATE({ state, dispatch, rootState }, payload) {
    const { promoterOid } = rootState.authModule.account!
    const automationId = payload?.id ? payload.id : state.id!
    const { $api } = getContext()
    await $api.automation.duplicate(promoterOid, automationId)
    dispatch('FETCH_AUTOMATIONS')
  },
  async FETCH_AUTOMATIONS({ commit, rootState }) {
    const { promoterOid } = rootState.authModule.account!
    const { $api } = getContext()

    try {
      commit('setLoading', true)
      const data = await $api.automation.fetchAutomations(promoterOid)
      commit('setAutomationsList', data)
    }
    catch (error) {
      console.log(`There was an error when trying to fetch all the automations: `, error)
    }
    finally {
      commit('setLoading', false)
    }
  },
  CLEAR_AUTOMATION({ commit }) {
    commit('clearAutomation')
  },
  CLEAR_TICKET_CLASSES({ commit }) {
    commit('clearTicketClasses')
  },
  async FETCH_TAGS({ commit, rootState }, { filterString }) {
    const { promoterOid } = rootState.authModule.account!
    const { $api } = getContext()
    try {
      const tags = await $api.tags.get(promoterOid, { filterString })
      commit('tagModule/SET_TAGS', tags, { root: true })
      return tags
    }
    catch (error) {
      console.error(error)
    }
  },
  async SET_TRIGGERS({ dispatch, commit }, { data, promoterOid }) {
    if (!Object.keys(data.triggers).length) {
      return
    }
    const formattedTriggers = await getTriggers(data.triggers, promoterOid, data.chart.oid)
    let i = 0
    while (i < formattedTriggers.length) {
      commit('addTrigger', formattedTriggers[i])
      i++
    }
    dispatch('PRE_FETCH_TRIGGER_LISTS', formattedTriggers)
  },
  // pre fetch list data here to populate relevant dropdowns
  // to stop selected item/UI flashing while fetching live
  async PRE_FETCH_TRIGGER_LISTS({ dispatch }, triggers) {
    let i = triggers.length
    while (i--) {
      switch (triggers[i].key) {
        case 'purchased.product-from-store':
          dispatch('FETCH_PRODUCTS')
          break
      }
    }
  },
  async FETCH_AUTOMATION_DATA({ rootState }, id) {
    const { promoterOid } = rootState.authModule.account!
    const { $api } = getContext()
    try {
      const data = await $api.automation.fetchAutomation(promoterOid, id)
      return data
    }
    catch (error) {
      console.log(`There was an error when trying to fetch the automation with id ${id}: `, error)
    }
  },
  async FETCH_AND_SET_AUTOMATION({ dispatch, commit, rootState }, {
    id,
    clearActionsBeforeSet = false,
    skipSetTriggers = false,
  }) {
    const { promoterOid } = rootState.authModule.account!
    const { $arNotification, $router } = getContext()

    try {
      commit('setLoading', true)
      const data = await dispatch('FETCH_AUTOMATION_DATA', id)

      if (!data?.chart) {
        $arNotification.push({
          type: 'warning',
          message: 'Automation does not exist or has been deleted.',
        })
        $router.push('/automation')
        return
      }

      if (clearActionsBeforeSet) {
        commit('clearActions')
      }
      commit('setAutomationBasicDetails', { chart: data.chart, rootBoxId: data['root-box-oid'] })

      dispatch('SET_CHILD_ACTIONS', data)
      if (!skipSetTriggers) dispatch('SET_TRIGGERS', { data, promoterOid })
    }
    catch (error) {
      console.log(`There was an error when trying to fetch the automation with id ${id}: `, error)
    }
    finally {
      commit('setLoading', false)
    }
  },
  SET_LOADING({ commit }, bool) {
    commit('setLoading', bool)
  },
  async SET_CHILD_ACTIONS({ state, commit, rootState }, data) {
    const { promoterOid } = rootState.authModule.account!
    const rootBoxId = state.rootBoxId!
    const outIds = []
    const boxes: any = []
    let actionRowsCount = 1

    if (!outIds.length) {
      outIds.push({ id: rootBoxId })
    }

    const buildChart = async (outIds: any) => {
      const localBoxes = []
      const nextOutIds = []
      let i = 0
      while (i < outIds.length) {
        let actionBox, formattedActionBox, selfBoxId, parentOutBoxId

        if (outIds[i]?.type === 'placeholder') {
          // placeholder, add placeholder block in this row also
          localBoxes.push({ type: 'placeholder' })
          nextOutIds.push({ type: 'placeholder' })
          i++
          continue
        }

        const link = data.links['by-from-box-oid'][outIds[i]?.id]
        if (!link) {
          // we've hit the bottom of this branch, add a prompt block
          localBoxes.push({ type: 'action-prompt', parentOutBoxId: outIds[i].id, branch: outIds[i]?.branch || null })
          nextOutIds.push({ type: 'placeholder' })
        }
        else {
          // we have a valid link and box
          const box = data.boxes[link['to-box-oid']]
          // if (!box || !box?.['block-oid']) {
          //   debugger
          // }
          const block = data.blocks[box['block-oid']]
          parentOutBoxId = link['from-box-oid']
          selfBoxId = data.blocks[data.boxes[link['to-box-oid']]['block-oid']].boxes.self[0].oid

          if (block.type === 'control.if-else') {
            let thenBoxId, elseBoxId
            block.boxes.out.filter((item: any) => {
              if (item.name === 'then') {
                thenBoxId = item.oid
              }
              else {
                elseBoxId = item.oid
              }
            })

            actionBox = {
              id: {
                self: selfBoxId,
                outElse: elseBoxId,
                outThen: thenBoxId,
              },
              parentOutBoxId,
              block: {
                ...data.blocks[data.boxes[link['to-box-oid']]['block-oid']],
                parentOutBoxId,
              },
            }

            formattedActionBox = await formattedAPIBox(actionBox, promoterOid, data.chart.oid)

            localBoxes.push({ ...formattedActionBox, branch: outIds[i]?.branch || null })
            nextOutIds.push({ id: thenBoxId, branch: 'TRUE' })
            nextOutIds.push({ id: elseBoxId, branch: 'FALSE' })
          }
          else {
            const outBoxId = data.blocks[data.boxes[link['to-box-oid']]['block-oid']].boxes.out[0].oid

            actionBox = {
              id: {
                self: selfBoxId,
                out: outBoxId,
              },
              parentOutBoxId,
              block: {
                ...data.blocks[data.boxes[link['to-box-oid']]['block-oid']],
                parentOutBoxId,
              },
            }

            formattedActionBox = await formattedAPIBox(actionBox, promoterOid, data.chart.oid)
            localBoxes.push({ ...formattedActionBox, branch: outIds[i]?.branch || null })
            nextOutIds.push({ id: block.boxes.out[0].oid })
          }
        }

        i++
      }

      boxes.push(localBoxes)
      if (localBoxes.length > actionRowsCount) {
        actionRowsCount = localBoxes.length
      }
      const nextOutIdsActual = nextOutIds.filter((item) => {
        return item?.id
      })
      if (!nextOutIdsActual.length) {
        outIds = null
      }
      else {
        outIds = nextOutIds
      }

      if (!outIds) {
        commit('addBoxes', boxes)
      }
      else {
        await buildChart(outIds)
      }
    }

    await buildChart(outIds)
  },

  async ADD_TRIGGER({ state, commit, rootState, dispatch }, trigger) {
    const { promoterOid } = rootState.authModule.account!
    const automationId = state.id!
    const itemType = `${trigger.type}.${trigger.key}`
    const { $api } = getContext()

    try {
      const resp = await $api.automation.addTrigger(promoterOid, automationId, itemType)
      trigger.id = resp.oid

      if (state.triggers.items.length === 0) {
        dispatch('FETCH_AND_SET_AUTOMATION', { id: automationId, clearActionsBeforeSet: true })
        return
      }

      commit('addTrigger', trigger)
      commit('setEditableItem', trigger)
    }
    catch (error) {
      console.log('There was an error trying to create a trigger: ', error)
    }
  },
  async ADD_ACTION({ state, dispatch, commit, rootState }, { box, parentId }) {
    const { promoterOid } = rootState.authModule.account!
    const automationId = state.id!
    const blockType = `${box.block.type}.${box.block.key}`
    const { $api } = getContext()

    try {
      const resp = await $api.automation.addBlock(promoterOid, automationId, blockType, parentId)
      const { full, ...createdBlock } = resp
      const outBoxId = createdBlock.boxes.find((item: any) => item.type === 'out').oid

      const actionBox = {
        ...box,
        id: {
          self: createdBlock['parent-link']['to-box-oid'],
          out: outBoxId,
        },
        parentId,
        block: {
          ...box.block,
          id: createdBlock.block.oid,
          parentId,
        },
      }

      dispatch('SET_CHILD_ACTIONS', full)
      commit('setEditableItem', actionBox.block)
    }
    catch (error) {
      console.log('There was an error trying to create an action: ', error)
      throw (error)
    }
  },
  async REORDER_ACTIONS({ state, dispatch, commit, rootState }, { parentOutboxId, box }) {
    const { promoterOid } = rootState.authModule.account!
    const automationId = state.id!
    const { $api } = getContext()

    const data = await $api.automation.reorderBlock(promoterOid, automationId, box.block.id, parentOutboxId)
    dispatch('SET_CHILD_ACTIONS', data)
    commit('setEditableItem', box.block)
    commit('clearDragOverId')
    commit('clearDraggedBox')
  },
  async DELETE_TRIGGER({ state, commit, rootState }, { id, index }) {
    const { promoterOid } = rootState.authModule.account!
    const automationId = state.id!
    const { $api } = getContext()

    try {
      await $api.automation.deleteTrigger(promoterOid, automationId, id)
      commit('removeTriggerFromState', index)
      if (state.editableItem?.id === id) {
        commit('clearEditable')
      }
    }
    catch (error) {
      console.log(error)
    }
  },
  async DELETE_ACTION({ state, dispatch, commit, rootState }, box) {
    const { promoterOid } = rootState.authModule.account!
    const automationId = state.id!
    const { $api } = getContext()

    try {
      await $api.automation.deleteBlock(promoterOid, automationId, box.block.id)
      const data = await dispatch('FETCH_AUTOMATION_DATA', automationId)
      if (state.editableItem?.id === box.block.id) {
        commit('clearEditable')
      }
      dispatch('SET_CHILD_ACTIONS', data)
    }
    catch (error) {
      throw error
    }
  },
  async FETCH_AUTOMATION_STATUS_SINGLE({ state, commit, rootState }, payload) {
    let withCommit: boolean = true
    const { promoterOid } = rootState.authModule.account!
    let automationId
    if (payload) {
      automationId = payload.id
      withCommit = payload.withCommit
    }
    else {
      automationId = state.id!
    }
    const { $api } = getContext()

    try {
      const resp = await $api.automation.fetchAutomationSimple(promoterOid, automationId)
      if (resp === '') return

      if (withCommit) {
        commit('updateChartStatus', resp.status)
      }
      return resp.status
    }
    catch (error) {
      console.log('Error polling status of single automation:', error)
    }
  },
  async FETCH_AUTOMATION_STATUS_LIST({ state, commit, rootState }) {
    const { promoterOid } = rootState.authModule.account!
    const { $api } = getContext()

    try {
      const automations = await $api.automation.fetchAutomations(promoterOid)
      let i = automations.length
      while (i--) {
        commit('updateListItemStatus', { id: automations[i].oid, status: automations[i].status })
      }
    }
    catch (error) {
      console.log('Error polling status of automation list:', error)
    }
  },
  CLEAR_AUTOMATIONS_LIST({ commit }) {
    commit('clearAutomationsList')
  },
  SET_NEW_ACTION_IS_DRAGGING({ commit }, bool) {
    commit('setNewActionIsDragging', bool)
  },
  SET_DRAG_OVER_ID({ commit }, id) {
    commit('setDragOverId', id)
  },
  CLEAR_DRAG_OVER_ID({ commit }) {
    commit('clearDragOverId')
  },
  SET_DRAGGED_BOX({ commit }, box) {
    commit('setDraggedBox', box)
    commit('clearEditable')
  },
  // TODO: remove
  SET_DRAGGED_BLOCK({ state, commit }, id) {
    const block = getActionBlock(state.actions, id)
    commit('setDraggedBlock', block)
    commit('clearEditable')
  },
  CLEAR_DRAGGED_BOX({ commit }) {
    commit('clearDraggedBox')
  },
  CLEAR_DRAGGED_BLOCK({ commit }) {
    commit('clearDraggedBlock')
  },
  SET_SIDEBAR_TAB({ commit }, tab) {
    commit('setSidebarTab', tab)
  },
  SET_ZOOM({ commit }, val) {
    commit('setZoom', val)
  },
  SET_SCROLL_VALS({ commit }, data) {
    commit('setScrollVals', data)
  },
  SET_IS_SCROLLING({ commit }, val) {
    commit('setIsScrolling', val)
  },
  TOGGLE_FULL_SCREEN({ commit }) {
    commit('toggleFullScreen')
  },
  async FETCH_CAMPAIGN_REGISTRATION({}, { promoterOid, oid }) {
    const { $api } = getContext()
    const data = await $api.campaign.fetchCampaignForAutomation(promoterOid, oid)
    return data
  },

  async SET_AUDIENCE_FILTER_AS_CONDITION({ state, commit, rootState }, { id, filter }) {
    if (!rootState.authModule.account) return
    const { promoterOid } = rootState.authModule.account
    const automationId = state.id!
    const actionBlockId = state.editableItem!.id
    const name = `automation-ifelse-filter-${promoterOid}-${automationId}-${actionBlockId}-${Date.now()}`

    const filterGroup: any = {
      conditions: [filter],
      logic: [],
    }

    const actionBox = getAutomationAction(state.actions, id)
    const { $api } = getContext()

    try {
      const filterGroupResp = await $api.automation.createFilterGroup(promoterOid, name, filterGroup, false, false, 2, automationId)
      const config = {
        'filter-group-oid': filterGroupResp.oid,
        'frontend': {
          segment: false,
        },
      }
      await $api.automation.addIfElseFilter(promoterOid, automationId, id, config)

      commit('updateIfElseContextData', { item: actionBox.block, config, meta: { filter } })
      commit('actionConfigHasUpdated')
    }
    catch (error) {
      console.log(error)
    }
  },

  async RESET_IF_ELSE_CONFIG({ state, commit, rootState }, id) {
    if (!rootState.authModule.account) return
    const { promoterOid } = rootState.authModule.account
    const automationId = state.id!
    const { $api } = getContext()

    await $api.automation.addIfElseFilter(promoterOid, automationId, id, {})
    const actionBox = getAutomationAction(state.actions, id)
    if (!actionBox) {
      throw new Error(`Unable to find action block with id: ${id}`)
    }
    commit('updateIfElseContextData', { item: actionBox.block, config: null, meta: {} })
    commit('actionConfigHasUpdated')
  },

  async SET_SAVED_SEGMENT_AS_CONDITION({ state, commit, rootState }, { id, segmentId }) {
    if (!rootState.authModule.account) return
    const { promoterOid } = rootState.authModule.account
    const automationId = state.id!

    const config = {
      'filter-group-oid': segmentId,
      'frontend': {
        segment: true,
      },
    }
    const { $api } = getContext()

    await $api.automation.addIfElseFilter(promoterOid, automationId, id, config)
    const actionBox = getAutomationAction(state.actions, id)
    if (!actionBox) {
      throw new Error(`Unable to find action block with id: ${id}`)
    }
    commit('updateIfElseContextData', { item: actionBox.block, config, meta: {} })
    commit('actionConfigHasUpdated')
  },
  async UPDATE_FILTER_CONDITION({ state, commit, rootState }, { id, filterId, filter }) {
    if (!rootState.authModule.account) return
    const { promoterOid } = rootState.authModule.account
    const automationId = state.id!

    const filterGroup: any = {
      conditions: [filter],
      logic: [],
    }
    const { $api } = getContext()

    try {
      await $api.automation.updateFilterCondition(promoterOid, filterId, filterGroup)
      const actionBox = getAutomationAction(state.actions, id)
      if (!actionBox) {
        throw new Error(`Unable to find action block with id: ${id}`)
      }

      commit('updateIfElseContextData', {
        item: actionBox.block,
        config: { ...actionBox.block.config, frontend: { segment: false } },
        meta: { filter },
      })
    }
    catch (error) {
      console.log('An error occurred when attempting to update a filter condition for Automation: ', error)
    }
  },
  async FETCH_MESSAGE_LISTS({ commit, rootState }) {
    const promoterOid = rootState.authModule.account?.promoterOid!
    let messageLists
    const { $api } = getContext()
    try {
      messageLists = await $api.automation.getMessageLists(promoterOid)
      commit('messageListModule/SET_MESSAGE_LISTS', messageLists, { root: true })
    }
    catch (error) {
      console.error(error)
    }
  },
  async FETCH_MANUAL_MESSAGE_LISTS({ commit, rootState }) {
    const promoterOid = rootState.authModule.account?.promoterOid!
    let messageLists
    const { $api } = getContext()
    try {
      messageLists = await $api.automation.getManualMessageLists(promoterOid)
      commit('messageListModule/SET_MESSAGE_LISTS', messageLists, { root: true })
    }
    catch (error) {
      console.error(error)
    }
  },
  /**
   * Fetches the campaign list. If the campaigns are already stored in state,
   * it simply returns the campaigns from state. Otherwise, it fetches the
   * campaigns from the API and stores them in state.
   */
  async FETCH_CAMPAIGN_LIST({ commit, rootState }) {
    const promoterOid = rootState.authModule.account?.promoterOid!
    if (!promoterOid) return
    let campaigns
    if (rootState.campaign?.campaigns?.length > 0) {
      campaigns = rootState.campaign.campaigns
    }
    else {
      const { $api } = getContext()
      try {
        campaigns = await $api.automation.getCampaigns(promoterOid)
        commit('campaignModule/SET_CAMPAIGNS', campaigns, { root: true })
      }
      catch (error) {
        console.error(error)
      }
    }
  },
  async FETCH_PRODUCTS({ commit, rootState }) {
    if (!rootState.authModule.account) return
    const { promoterOid } = rootState.authModule.account
    const { $api } = getContext()

    const data = await $api.automation.getProducts(promoterOid)
    commit('setProducts', data)
  },
  SET_TRIGGER_IS_MOVING({ commit }, bool) {
    commit('setTriggerIsMoving', bool)
  },
  async FETCH_TICKET_CLASSES({ commit, rootState }, { eventOid, top } = {}) {
    const { promoterOid } = rootState.authModule.account
    const { $api, $axios } = getContext()
    try {
      const data = await $api.automation.getTicketTypes(promoterOid, eventOid, top)

      // If no eventOid provided, this is a general request for all ticket classes
      // Store the result in state so it can be used for displaying filter tags, etc.
      if (eventOid === undefined) {
        commit('setTicketClasses', data)
      }

      return data
    }
    catch (error) {
      console.error(error)
    }
  },
  async CREATE_MESSAGE_LIST(
    { commit, rootState },
    { name, sms, email, facebookMessenger, meta, filterGroupOid },
  ) {
    const { $api } = getContext()
    const { $axios, $arNotification } = getContext()

    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid, oid } = rootState.authModule.account

    const defaultData = {
      promoterOid,
      promoterAccountOid: oid,
      userDefined: true,
      sysActivep: true,
    }

    const messageListData = {
      name,
      sms,
      email,
      facebookMessenger,
      meta,
      filterGroupOid: filterGroupOid || null,
      ...defaultData,
    }

    try {
      await $axios.post(`/promoter/${promoterOid}/message-list`, messageListData)
      $arNotification.push({
        type: 'success',
        message: 'Successfully created list',
      })

      await this.dispatch('automationModule/FETCH_MANUAL_MESSAGE_LISTS')
      return true
    }
    catch (error: any) {
      console.error(error)
      if (error.response) {
        if (error.response.status === 409) {
          $arNotification.push({
            type: 'error',
            message: 'Could not create new list. Please ensure that you have chosen a unique name and filter',
          })
        }
        else {
          $arNotification.push({ type: 'error', message: 'Failed to save list' })
        }
      }
      return false
    }
    finally {
      commit('setDisplayCreateMessageListModal', false)
    }
  },
  async FETCH_AUTOMATION_INSIGHTS({ commit, rootState }, { automationOid }) {
    const { promoterOid } = rootState.authModule.account
    const { $api } = getContext()

    if (!promoterOid || !automationOid) {
      console.error('No promoterOid or automationOid provided')
      return
    }
    try {
      const data = await $api.automation.fetchAutomationInsights(promoterOid, automationOid)
      commit('setAutomationInsights', data)
      return data
    }
    catch (error) {
      console.error(error)
    }
  },
  UPDATE_EDIT_ENABLED({ commit }, bool) {
    commit('updateEditEnabled', bool)
  },
  async SET_ERRORS({ state, dispatch, commit, rootState }, errorMessage) {
    try {
      commit('setErrors', errorMessage)
    }
    catch (error) {
      console.error('There was an error trying to create an action: ', error)
      throw (error)
    }
  },
}
